import { useRouter } from "next/router";
import React, { ChangeEvent, useState } from "react";
import {
  Alert,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Input,
  Row,
} from "reactstrap";
import * as Yup from "yup";
import Button from "~/components/Button";
import CheckboxField from "~/components/CheckboxField";
import Form from "~/components/Form";
import GlobalAuthHeader from "~/components/GlobalAuthHeader";
import TextField from "~/components/TextField";
import useApi from "~/hooks/use-api";
import type { Provider } from "~/gen/rpc/sso";

export type ProviderFormValues = {
  name: string;
  issuerUrl: string;
  clientId: string;
  clientSecret?: string;
  exclusive: boolean;
  enabled?: boolean;
};

export const initialProvider: ProviderFormValues = {
  name: "",
  issuerUrl: "",
  clientId: "",
  clientSecret: "",
  exclusive: false,
  enabled: false,
};

const baseProviderSchema = {
  name: Yup.string().required("Name is required"),
  issuerUrl: Yup.string()
    .url("Issuer URL must be a valid URL")
    .required("Issuer URL is required"),
  clientId: Yup.string().required("Client ID is required"),
  exclusive: Yup.boolean(),
  enabled: Yup.boolean(),
};

export const newProviderSchema = Yup.object<ProviderFormValues>({
  ...baseProviderSchema,
  clientSecret: Yup.string().required("Client secret is required"),
});

export const editProviderSchema = Yup.object<ProviderFormValues>({
  ...baseProviderSchema,
  clientSecret: Yup.string(),
});

export function ProviderFormFields({ isNew }: { isNew?: boolean }) {
  return (
    <>
      <Row>
        <TextField col="6" label="Name" name="name" />
        <TextField
          col="6"
          label="Issuer URL"
          name="issuerUrl"
          placeholder="https://accounts.google.com"
        />
      </Row>
      <Row>
        <TextField col="6" label="Client ID" name="clientId" />
        <TextField
          col="6"
          label="Client Secret"
          name="clientSecret"
          placeholder={isNew ? undefined : "Leave blank to keep existing"}
          help={isNew ? undefined : "Only set this to rotate the secret"}
        />
      </Row>
      <Row>
        {!isNew && (
          <CheckboxField
            col="6"
            label="Enabled"
            name="enabled"
            description="Disable to prevent users authenticating via this provider — logins fall back to password"
          />
        )}
        <CheckboxField
          col="6"
          label="Exclusive"
          name="exclusive"
          description="Created for a single domain — once linked it won't be available for other domains"
        />
      </Row>
    </>
  );
}

export default function SsoProviderPage(): JSX.Element {
  const router = useRouter();
  const providerId = router.query.providerId as string;

  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: provider, revalidate } = useApi<Provider>(
    providerId && `/api/global-auth/sso-providers/${providerId}`
  );

  return (
    <>
      <GlobalAuthHeader active="sso" />
      <Container>
        <Row>
          <Col>
            <h2>
              {provider?.name ?? providerId}{" "}
              <small className="text-muted">{providerId}</small>
            </h2>
          </Col>
          <Col sm="auto">
            <DeleteModal
              providerId={providerId}
              providerName={provider?.name}
              open={confirmOpen}
              handleClose={() => setConfirmOpen(false)}
            />
            <Button danger primary onClick={() => setConfirmOpen(true)}>
              Delete
            </Button>
          </Col>
        </Row>

        <Form
          initValues={initialProvider}
          path={`/api/global-auth/sso-providers/${providerId}`}
          validationSchema={editProviderSchema}
          onSave={() => revalidate()}
        >
          <ProviderFormFields />
        </Form>
      </Container>
    </>
  );
}

function DeleteModal(props: {
  open: boolean;
  handleClose: () => void;
  providerId: string;
  providerName?: string;
}) {
  const router = useRouter();
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInput(event.target.value);
  };

  const deleteProvider = async () => {
    const res = await fetch(
      `/api/global-auth/sso-providers/${props.providerId}`,
      { method: "DELETE" }
    );

    if (res.ok) {
      router.push("/global-auth/sso/providers");
      return;
    }

    let message = "Failed to delete provider";
    try {
      const body = await res.json();
      if (body.message) message = body.message;
    } catch {
      // ignore
    }
    setError(message);
  };

  return (
    <Modal isOpen={props.open} toggle={props.handleClose}>
      <ModalHeader toggle={props.handleClose}>
        Are you absolutely sure?
      </ModalHeader>
      <ModalBody>
        <Row form>
          <Col lg={12}>
            {error && <Alert color="danger">{error}</Alert>}
            <Alert color="warning">
              Unexpected bad things will happen if you don’t read this!
            </Alert>
            <p>
              This action cannot be undone. This will permanently delete the{" "}
              {props.providerName} provider.
            </p>
            <p>
              Please type <code>{props.providerName}</code> to confirm
            </p>

            <Input col="12" name="confirm" onChange={handleChange} />

            <br />

            <Button
              style={{ width: "100%" }}
              danger
              primary
              disabled={input !== props.providerName}
              onClick={deleteProvider}
            >
              I understand the consequences, delete this provider
            </Button>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
}
